// Automatic Action State Types and Configuration System

export enum AutomaticStateType {
  SEND_EMAIL_SES = 'send_email_ses',
  SEND_EMAIL_SMTP = 'send_email_smtp', 
  GENERATE_PDF = 'generate_pdf',
  SEND_NOTIFICATION_SNS = 'send_notification_sns',
  DROP_MESSAGE_SQS = 'drop_message_sqs',
  STORE_FILE_S3 = 'store_file_s3',
  CALL_LAMBDA = 'call_lambda'
}

// Database IDs for automatic state types
export enum AutomaticStateTypeId {
  SEND_EMAIL_SES = 1,
  SEND_EMAIL_SMTP = 2,
  GENERATE_PDF = 3,
  SEND_NOTIFICATION_SNS = 4,
  DROP_MESSAGE_SQS = 5,
  STORE_FILE_S3 = 6,
  CALL_LAMBDA = 7
}

// Mapping between string enum and database ID
export const STATE_TYPE_TO_ID: Record<AutomaticStateType, number> = {
  [AutomaticStateType.SEND_EMAIL_SES]: AutomaticStateTypeId.SEND_EMAIL_SES,
  [AutomaticStateType.SEND_EMAIL_SMTP]: AutomaticStateTypeId.SEND_EMAIL_SMTP,
  [AutomaticStateType.GENERATE_PDF]: AutomaticStateTypeId.GENERATE_PDF,
  [AutomaticStateType.SEND_NOTIFICATION_SNS]: AutomaticStateTypeId.SEND_NOTIFICATION_SNS,
  [AutomaticStateType.DROP_MESSAGE_SQS]: AutomaticStateTypeId.DROP_MESSAGE_SQS,
  [AutomaticStateType.STORE_FILE_S3]: AutomaticStateTypeId.STORE_FILE_S3,
  [AutomaticStateType.CALL_LAMBDA]: AutomaticStateTypeId.CALL_LAMBDA,
};

// Mapping between database ID and string enum
export const ID_TO_STATE_TYPE: Record<number, AutomaticStateType> = {
  [AutomaticStateTypeId.SEND_EMAIL_SES]: AutomaticStateType.SEND_EMAIL_SES,
  [AutomaticStateTypeId.SEND_EMAIL_SMTP]: AutomaticStateType.SEND_EMAIL_SMTP,
  [AutomaticStateTypeId.GENERATE_PDF]: AutomaticStateType.GENERATE_PDF,
  [AutomaticStateTypeId.SEND_NOTIFICATION_SNS]: AutomaticStateType.SEND_NOTIFICATION_SNS,
  [AutomaticStateTypeId.DROP_MESSAGE_SQS]: AutomaticStateType.DROP_MESSAGE_SQS,
  [AutomaticStateTypeId.STORE_FILE_S3]: AutomaticStateType.STORE_FILE_S3,
  [AutomaticStateTypeId.CALL_LAMBDA]: AutomaticStateType.CALL_LAMBDA,
};

export enum ParameterDataSource {
  MODULE_DATA = 'module_data',        // Properties of current module (e.g., timeoff fields)
  PREVIOUS_OUTPUTS = 'previous_outputs', // Outputs from previous workflow steps
  USER_INFO = 'user_info',            // Current user information
  CUSTOM_INPUT = 'custom_input',      // Custom text, numbers, etc.
  FORMULA = 'formula'                 // Applied formulas
}

export enum ActionOutcome {
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE'
}

export interface ParameterDefinition {
  id: string;
  name: string;
  type: 'string' | 'number' | 'boolean' | 'email' | 'url' | 'json' | 'file';
  required: boolean;
  description?: string;
  defaultValue?: any;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    allowedValues?: any[];
  };
}

export interface DataSourceOption {
  source: ParameterDataSource;
  path: string;           // JSONPath or property path
  displayName: string;
  dataType: string;
  description?: string;
}

export interface ParameterBinding {
  parameterId: string;
  dataSource: ParameterDataSource;
  sourcePath?: string;    // Path to the data (e.g., "timeoff.startDate")
  customValue?: any;      // For custom inputs
  formula?: string;       // For formula-based values
  transformation?: string; // Optional data transformation
}

export interface AutomaticStateConfig {
  stateType: AutomaticStateType;
  parameters: ParameterBinding[];
  successTransition?: string;  // Next state ID on success
  failureTransition?: string;  // Next state ID on failure
  retryConfig?: {
    maxRetries: number;
    retryDelay: number;
    backoffMultiplier?: number;
  };
}

// State Type Definitions with their required parameters
export const STATE_TYPE_DEFINITIONS: Record<AutomaticStateType, {
  name: string;
  description: string;
  icon: string;
  parameters: ParameterDefinition[];
  outputSchema: any;
}> = {
  [AutomaticStateType.SEND_EMAIL_SES]: {
    name: 'Send Email (SES)',
    description: 'Send email using Amazon Simple Email Service',
    icon: 'EmailIcon',
    parameters: [
      {
        id: 'to',
        name: 'To Address(es)',
        type: 'email',
        required: true,
        description: 'Recipient email address(es)'
      },
      {
        id: 'subject',
        name: 'Subject',
        type: 'string',
        required: true,
        description: 'Email subject line'
      },
      {
        id: 'body',
        name: 'Email Body',
        type: 'string',
        required: true,
        description: 'Email content (HTML or plain text)'
      },
      {
        id: 'from',
        name: 'From Address',
        type: 'email',
        required: false,
        description: 'Sender email address (uses default if not specified)'
      }
    ],
    outputSchema: {
      messageId: 'string',
      timestamp: 'datetime',
      status: 'string'
    }
  },
  
  [AutomaticStateType.SEND_EMAIL_SMTP]: {
    name: 'Send Email (SMTP)',
    description: 'Send email using SMTP server',
    icon: 'EmailIcon',
    parameters: [
      {
        id: 'to',
        name: 'To Address(es)',
        type: 'email',
        required: true,
        description: 'Recipient email address(es)'
      },
      {
        id: 'subject',
        name: 'Subject',
        type: 'string',
        required: true,
        description: 'Email subject line'
      },
      {
        id: 'body',
        name: 'Email Body',
        type: 'string',
        required: true,
        description: 'Email content'
      },
      {
        id: 'smtpServer',
        name: 'SMTP Server',
        type: 'string',
        required: true,
        description: 'SMTP server hostname'
      }
    ],
    outputSchema: {
      messageId: 'string',
      timestamp: 'datetime',
      status: 'string'
    }
  },

  [AutomaticStateType.GENERATE_PDF]: {
    name: 'Generate PDF File',
    description: 'Generate PDF document from template',
    icon: 'PictureAsPdfIcon',
    parameters: [
      {
        id: 'template',
        name: 'PDF Template',
        type: 'string',
        required: true,
        description: 'Template name or path'
      },
      {
        id: 'data',
        name: 'Template Data',
        type: 'json',
        required: true,
        description: 'Data to populate the template'
      },
      {
        id: 'fileName',
        name: 'File Name',
        type: 'string',
        required: false,
        description: 'Generated PDF file name'
      }
    ],
    outputSchema: {
      filePath: 'string',
      fileSize: 'number',
      timestamp: 'datetime'
    }
  },

  [AutomaticStateType.SEND_NOTIFICATION_SNS]: {
    name: 'Send Notification (SNS)',
    description: 'Send notification via Amazon SNS',
    icon: 'NotificationsIcon',
    parameters: [
      {
        id: 'topicArn',
        name: 'SNS Topic ARN',
        type: 'string',
        required: true,
        description: 'Amazon SNS topic ARN'
      },
      {
        id: 'message',
        name: 'Message',
        type: 'string',
        required: true,
        description: 'Notification message'
      },
      {
        id: 'subject',
        name: 'Subject',
        type: 'string',
        required: false,
        description: 'Message subject (for email notifications)'
      }
    ],
    outputSchema: {
      messageId: 'string',
      timestamp: 'datetime',
      status: 'string'
    }
  },

  [AutomaticStateType.DROP_MESSAGE_SQS]: {
    name: 'Drop Message into Queue (SQS)',
    description: 'Send message to Amazon SQS queue',
    icon: 'QueueIcon',
    parameters: [
      {
        id: 'queueUrl',
        name: 'SQS Queue URL',
        type: 'url',
        required: true,
        description: 'Amazon SQS queue URL'
      },
      {
        id: 'messageBody',
        name: 'Message Body',
        type: 'json',
        required: true,
        description: 'Message content'
      },
      {
        id: 'delaySeconds',
        name: 'Delay Seconds',
        type: 'number',
        required: false,
        description: 'Message delivery delay'
      }
    ],
    outputSchema: {
      messageId: 'string',
      timestamp: 'datetime',
      queueUrl: 'string'
    }
  },

  [AutomaticStateType.STORE_FILE_S3]: {
    name: 'Store File in S3',
    description: 'Upload file to Amazon S3 bucket',
    icon: 'CloudUploadIcon',
    parameters: [
      {
        id: 'bucket',
        name: 'S3 Bucket',
        type: 'string',
        required: true,
        description: 'S3 bucket name'
      },
      {
        id: 'key',
        name: 'Object Key',
        type: 'string',
        required: true,
        description: 'S3 object key (file path)'
      },
      {
        id: 'content',
        name: 'File Content',
        type: 'file',
        required: true,
        description: 'File content or data to store'
      },
      {
        id: 'contentType',
        name: 'Content Type',
        type: 'string',
        required: false,
        description: 'MIME type of the file'
      }
    ],
    outputSchema: {
      objectUrl: 'string',
      etag: 'string',
      timestamp: 'datetime'
    }
  },

  [AutomaticStateType.CALL_LAMBDA]: {
    name: 'Call Lambda Function',
    description: 'Invoke AWS Lambda function',
    icon: 'FunctionsIcon',
    parameters: [
      {
        id: 'functionName',
        name: 'Function Name',
        type: 'string',
        required: true,
        description: 'Lambda function name or ARN'
      },
      {
        id: 'payload',
        name: 'Payload',
        type: 'json',
        required: false,
        description: 'Function input payload'
      },
      {
        id: 'invocationType',
        name: 'Invocation Type',
        type: 'string',
        required: false,
        defaultValue: 'RequestResponse',
        validation: {
          allowedValues: ['RequestResponse', 'Event', 'DryRun']
        }
      }
    ],
    outputSchema: {
      statusCode: 'number',
      payload: 'json',
      timestamp: 'datetime'
    }
  }
};

// Available data sources for parameter binding
export interface ModuleDataSource {
  moduleName: string;
  fields: {
    name: string;
    type: string;
    displayName: string;
    description?: string;
  }[];
}

export interface WorkflowContext {
  moduleData: ModuleDataSource;
  previousOutputs: Record<string, any>; // Keyed by state ID
  userInfo: {
    id: string;
    name: string;
    email: string;
    roles: string[];
    department?: string;
    [key: string]: any;
  };
}

// Formula system for parameter calculations
export interface FormulaContext extends WorkflowContext {
  helpers: {
    date: any;      // Date manipulation functions
    string: any;    // String manipulation functions  
    math: any;      // Mathematical operations
    convert: any;   // Data conversion functions
  };
}

// Execution result interface
export interface ExecutionResult {
  outcome: ActionOutcome;
  output?: any;
  error?: string;
  executionTime: number;
  timestamp: Date;
}
